import {
  BACKEND_CODE_POLICY,
  isProfileImage,
  isVtoResult,
  type PrefetchItem,
  type ProfileImage,
  type RetryPolicy,
  type VtoBackendErrorCode,
  type VtoError,
  type VtoErrorCode,
  type VtoLookup,
  type VtoProgressStatus,
  type VtoStatus,
} from './vto-types.js';
import { createTimeout } from '../utils/with-timeout.js';

const GET_VTO_TIMEOUT_MILLIS = 60_000;
const PROFILE_IMAGE_TIMEOUT_MILLIS = 60_000;
const PREFETCH_TIMEOUT_MILLIS = 120_000;

// Cooldown durations specified by the BRTN bridge SLA. Codes not listed fall through to DEFAULT_COOLDOWN_MS.
const COOLDOWN_MS_BY_BACKEND_CODE: Record<string, number> = {
  CUSTOMER_RATE_LIMIT_EXCEEDED: 45_000,
  PROCESSING_TIMEOUT: 20_000,
  SERVICE_UNAVAILABLE: 15_000,
};

const DEFAULT_COOLDOWN_MS = 30_000;

const KNOWN_ERROR_CODES: ReadonlySet<string> = new Set<VtoErrorCode>([
  'invalidArgument',
  'unknownProfileImage',
  'generationFailed',
  'rateLimited',
  'networkError',
  'configurationFailed',
  'internalError',
  'userCanceled',
]);

const isKnownErrorCode = (raw: unknown): raw is VtoErrorCode => typeof raw === 'string' && KNOWN_ERROR_CODES.has(raw);

const isKnownBackendCode = (raw: unknown): raw is VtoBackendErrorCode =>
  typeof raw === 'string' && raw in BACKEND_CODE_POLICY;

const normalizeBackendErrorCode = (raw: unknown): VtoBackendErrorCode | undefined => {
  if (raw === undefined) return undefined;
  return isKnownBackendCode(raw) ? raw : 'UNKNOWN_ERROR';
};

const withTimeout = createTimeout(() =>
  Object.assign(new Error('VTO bridge timeout'), { code: 'networkError' as const }),
);

const statusEqual = (a: VtoStatus, b: VtoStatus): boolean => {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'cooldown' && b.kind === 'cooldown') return a.until === b.until;
  return true;
};

/**
 * Normalizes a raw value from the BRTN VTO bridge into a typed VtoError. Unknown error codes
 * become 'internalError'; unknown backendErrorCodes become 'UNKNOWN_ERROR'. The result is always
 * safe to pass to classifyError without a runtime exhaustiveness failure.
 */
export function normalizeBridgeError(raw: unknown): VtoError {
  const e = (raw ?? {}) as Partial<VtoError>;
  return {
    source: e.source,
    code: isKnownErrorCode(e.code) ? e.code : 'internalError',
    message: e.message,
    backendErrorCode: normalizeBackendErrorCode(e.backendErrorCode),
    syan: typeof e.syan === 'number' ? e.syan : undefined,
    colorId: typeof e.colorId === 'string' ? e.colorId : undefined,
  };
}

export function classifyError(error: VtoError): RetryPolicy {
  switch (error.code) {
    case 'invalidArgument':
      return { kind: 'permanent' };
    case 'configurationFailed':
      return { kind: 'vto-disabled' };
    case 'unknownProfileImage':
      return { kind: 'profile-recovery' };
    case 'rateLimited':
      return { kind: 'cooldown', cooldownMs: DEFAULT_COOLDOWN_MS };
    case 'networkError':
    case 'internalError':
      return { kind: 'transient' };
    case 'userCanceled':
      return { kind: 'no-op' };
    case 'generationFailed': {
      const sub = error.backendErrorCode;
      // Missing sub-code is treated like UNKNOWN_ERROR (transient) — same as an unknown sub-code
      // post-normalization. Caching null for a missing-field bridge bug would punish it harder
      // than an explicitly unknown one.
      const kind = sub ? BACKEND_CODE_POLICY[sub] : 'transient';
      if (kind === 'cooldown') {
        return { kind, cooldownMs: (sub && COOLDOWN_MS_BY_BACKEND_CODE[sub]) ?? DEFAULT_COOLDOWN_MS };
      }
      return { kind };
    }
    default:
      return error.code satisfies never;
  }
}

export class VtoService {
  private profileImage: ProfileImage | null = null;
  // Maps syan_colorId keys to: the in-flight Promise (while requesting), the resolved VtoResult (on
  // success), or null (on permanent error — other error kinds delete the key or clear the whole map).
  // Concurrent callers dedupe on the in-flight Promise via cache.get.
  private readonly cache = new Map<string, VtoLookup | Promise<VtoLookup>>();
  private currentStatus: VtoStatus = { kind: 'ok' };
  private cooldownUntil = 0;
  private cooldownTimer: ReturnType<typeof setTimeout> | undefined;
  private callbacksAttached = false;

  // Bound bridge callbacks so we can detect re-attachment by other consumers and detach in destroy().
  private readonly onBridgeError = (raw: unknown): void => this.handleError(normalizeBridgeError(raw));
  private readonly onBridgeProgress = (s: VtoProgressStatus): void => this.handleProgress(s);

  constructor(private readonly ttlMillis: number) {}

  isAvailable(): boolean {
    return typeof globalThis.brtn?.vto?.requestProfileImage === 'function';
  }

  async getProfileImage(): Promise<ProfileImage | null> {
    if (this.profileImage) return this.profileImage;
    if (!this.isAvailable()) return null;
    this.attachBridgeCallbacks();
    try {
      const raw = await withTimeout(globalThis.brtn!.vto!.getProfileImage(), PROFILE_IMAGE_TIMEOUT_MILLIS);
      const result = isProfileImage(raw) ? raw : null;
      this.profileImage = result;
      return result;
    } catch (e) {
      this.handleError({ ...normalizeBridgeError(e), source: 'getProfileImage' });
      return null;
    }
  }

  async requestProfileImage(): Promise<ProfileImage | null> {
    if (!this.isAvailable()) return null;
    this.attachBridgeCallbacks();
    // A user-initiated re-upload is a fresh attempt: clear any latched error status so this
    // attempt's outcome is reported afresh. Without this, a repeat error (e.g. another invalid
    // image) emits the same status and is swallowed by emitStatus's dedup — no snackbar fires.
    this.markRecovered();
    try {
      const raw = await withTimeout(globalThis.brtn!.vto!.requestProfileImage(), PROFILE_IMAGE_TIMEOUT_MILLIS);
      const result = isProfileImage(raw) ? raw : null;
      this.profileImage = result;
      this.cache.clear();
      return result;
    } catch (e) {
      const error = normalizeBridgeError(e);
      if (error.code === 'userCanceled') return null;
      this.handleError({ ...error, source: 'requestProfileImage' });
      this.profileImage = null;
      this.cache.clear();
      return null;
    }
  }

  async prefetchVtos(items: PrefetchItem[]): Promise<void> {
    if (!this.isAvailable() || items.length === 0) return;
    if (Date.now() < this.cooldownUntil) return;
    const profile = await this.getProfileImage();
    if (!profile) return;

    const chunks: PrefetchItem[][] = [];
    for (let i = 0; i < items.length; i += 100) chunks.push(items.slice(i, i + 100));

    // Sequential so the bridge isn't asked to chew on 3+ parallel large requests, and so we can
    // bail out as soon as a global error (cooldown, suspend, profile-invalid) fires.
    for (const chunk of chunks) {
      try {
        await withTimeout(
          globalThis.brtn!.vto!.prefetchVtos({ profileImage: profile, items: chunk, ttl: this.ttlMillis }),
          PREFETCH_TIMEOUT_MILLIS,
        );
      } catch (e: unknown) {
        this.handleError({ ...normalizeBridgeError(e), source: 'prefetchVtos' });
        if (this.currentStatus.kind !== 'ok') return;
      }
    }
  }

  async getVto(syan: number, colorId: string): Promise<VtoLookup> {
    if (!this.isAvailable()) return null;
    if (Date.now() < this.cooldownUntil) return null;
    this.attachBridgeCallbacks();

    const key = `${syan}_${colorId}`;
    const cached = this.cache.get(key);
    if (cached !== undefined) return cached;

    const pending = this.doGetVto(syan, colorId, key);
    this.cache.set(key, pending);
    return pending;
  }

  private async doGetVto(syan: number, colorId: string, key: string): Promise<VtoLookup> {
    const profile = await this.getProfileImage();
    if (!profile) {
      this.cache.delete(key);
      return null;
    }
    try {
      const raw = await withTimeout(
        globalThis.brtn!.vto!.getVto({ profileImage: profile, syan, colorId, ttl: this.ttlMillis }),
        GET_VTO_TIMEOUT_MILLIS,
      );
      if (!isVtoResult(raw)) {
        this.handleError({
          source: 'getVto',
          code: 'internalError',
          message: 'invalid VTO bridge response',
          syan,
          colorId,
        });
        // handleError → applyPolicy('transient') already deleted the cache entry.
        return null;
      }
      this.cache.set(key, raw);
      this.markRecovered();
      return raw;
    } catch (e: unknown) {
      const error: VtoError = { ...normalizeBridgeError(e), source: 'getVto', syan, colorId };
      if (error.code === 'userCanceled') {
        this.cache.delete(key);
        return null;
      }
      this.handleError(error);
      // After handleError, the entry is either a cached VtoResult (concurrent success), null
      // (permanent), or absent (transient/global drain). Re-read so concurrent winners still get the
      // real value back.
      const next = this.cache.get(key);
      return next instanceof Promise || next === undefined ? null : next;
    }
  }

  destroy(): void {
    clearTimeout(this.cooldownTimer);
    this.cooldownTimer = undefined;
    const vto = globalThis.brtn?.vto;
    if (vto?._onError === this.onBridgeError) vto._onError = undefined;
    if (vto?._onProgress === this.onBridgeProgress) vto._onProgress = undefined;
    this.callbacksAttached = false;
  }

  private attachBridgeCallbacks(): void {
    if (this.callbacksAttached || !this.isAvailable()) return;
    this.callbacksAttached = true;
    const vto = globalThis.brtn!.vto!;
    if (vto._onError && vto._onError !== this.onBridgeError) {
      console.warn('[vto]', 'replacing existing _onError handler');
    }
    if (vto._onProgress && vto._onProgress !== this.onBridgeProgress) {
      console.warn('[vto]', 'replacing existing _onProgress handler');
    }
    vto._onError = this.onBridgeError;
    vto._onProgress = this.onBridgeProgress;
  }

  private handleProgress(s: VtoProgressStatus): void {
    document.dispatchEvent(new CustomEvent('ents:vto:progress', { detail: s }));
  }

  private handleError(e: VtoError): void {
    if (e.code === 'userCanceled') return;
    console.error(
      '[vto]',
      e.source ?? 'getVto',
      e.code,
      e.syan ?? '-',
      e.colorId ?? '-',
      e.message ?? '',
      e.backendErrorCode ?? '',
    );
    this.applyPolicy(e, classifyError(e));
  }

  private applyPolicy(e: VtoError, policy: RetryPolicy): void {
    const key = e.syan !== undefined && e.colorId !== undefined ? `${e.syan}_${e.colorId}` : null;
    switch (policy.kind) {
      case 'no-op':
        return;
      case 'transient':
        if (key) this.cache.delete(key);
        return;
      case 'permanent':
        if (key) this.cache.set(key, null);
        return;
      case 'profile-recovery':
        this.profileImage = null;
        this.cache.clear();
        this.emitStatus({ kind: 'profile-invalid' });
        return;
      case 'vto-disabled':
      case 'session-suspend':
        clearTimeout(this.cooldownTimer);
        this.cooldownTimer = undefined;
        this.cooldownUntil = Number.POSITIVE_INFINITY;
        this.cache.clear();
        this.emitStatus({ kind: policy.kind === 'vto-disabled' ? 'vto-disabled' : 'session-suspended' });
        return;
      case 'cooldown':
        this.setCooldown(policy.cooldownMs);
        return;
      default:
        return policy satisfies never;
    }
  }

  private setCooldown(cooldownMs: number): void {
    // Session-suspend / vto-disabled are permanent (cooldownUntil = Infinity); cooldown can't override them.
    if (this.cooldownUntil === Number.POSITIVE_INFINITY) return;

    const wasInCooldown = Date.now() < this.cooldownUntil;
    const until = Date.now() + cooldownMs;
    this.cooldownUntil = Math.max(this.cooldownUntil, until);

    clearTimeout(this.cooldownTimer);
    this.cooldownTimer = setTimeout(() => {
      if (Date.now() >= this.cooldownUntil && this.cooldownUntil !== Number.POSITIVE_INFINITY) {
        this.cooldownUntil = 0;
        this.cooldownTimer = undefined;
        this.markRecovered();
      }
    }, this.cooldownUntil - Date.now());

    this.cache.clear();
    if (!wasInCooldown) this.emitStatus({ kind: 'cooldown', until: this.cooldownUntil });
  }

  private emitStatus(next: VtoStatus): void {
    if (statusEqual(this.currentStatus, next)) return;
    this.currentStatus = next;
    document.dispatchEvent(new CustomEvent('ents:vto:status', { detail: next }));
  }

  private markRecovered(): void {
    if (this.currentStatus.kind === 'vto-disabled' || this.currentStatus.kind === 'session-suspended') return;
    if (Date.now() < this.cooldownUntil) return;
    if (this.currentStatus.kind !== 'ok') this.emitStatus({ kind: 'ok' });
  }
}
